import L from "leaflet";
import { api, ApiError, get } from "./api";
import * as store from "./store";
import { syncSoon } from "./sync";
import { compressImage, DRUHY, druhFromLatin, h, norm, parcelAt, PODNOZE, spColor, toast, uuid } from "./util";
import { I } from "./icons";
import { KIND_LABEL, KINDS, STAVY, type Identification, type Photo, type PhotoKind, type Plant } from "./types";
import { photoSrc, type Ctx } from "./garden";
import { isDemo } from "./demo";

let identifyEnabled: boolean | null = null;
async function canIdentify() {
  if (isDemo()) return false;
  if (identifyEnabled !== null) return identifyEnabled;
  try { identifyEnabled = (await get<{ enabled: boolean }>("/api/identify/status")).enabled; } catch { return false; }
  return identifyEnabled;
}

type Editing = { id: string; isNew: boolean; doc: Plant; newPhotos: Photo[]; removed: Set<string>; kinds: Map<string, PhotoKind> };

export function openForm(ctx: Ctx, id: string | null, ll?: L.LatLng, template?: Partial<Plant>) {
  const existing = id ? ctx.plants.get(id) : undefined;
  const now = new Date().toISOString();
  const doc: Plant = existing ? structuredClone(existing) : {
    id: uuid(),
    garden_id: ctx.garden.id,
    cislo: ctx.nextNumber(),
    druh: template?.druh ?? "",
    odruda: template?.odruda ?? "",
    rok: template?.rok ?? null,
    podnoz: template?.podnoz ?? "",
    stav: template?.stav ?? "zdravy",
    poznamka: template?.poznamka ?? "",
    lat: ll!.lat,
    lng: ll!.lng,
    parcela: "",
    urceni: null,
    updated_at: now,
  };
  const ed: Editing = { id: doc.id, isNew: !existing, doc, newPhotos: [], removed: new Set(), kinds: new Map() };
  let nextKind: PhotoKind = "rostlina";
  let stav = doc.stav;
  let duplicateAfterSave = false;
  ctx.select(existing ? doc.id : null);

  const sheet = ctx.root.querySelector("#formSheet") as HTMLElement;
  const badge = h("span", { class: "badge" }, String(doc.cislo ?? "?"));
  badge.style.background = spColor(doc.druh);
  const title = h("h2", {}, existing ? ctx.label(doc) : (template ? "Kopie: " + ([template.druh, template.odruda].filter(Boolean).join(" – ") || "Nová rostlina") : "Nová rostlina"));
  const photoBox = h("div", { class: "photos" });
  const kindChips = h("div", { class: "chips", role: "group", "aria-label": "Co fotíš" });
  const fileCam = h("input", { type: "file", accept: "image/*", capture: "environment", hidden: true }) as HTMLInputElement;
  const fileGal = h("input", { type: "file", accept: "image/*", multiple: true, hidden: true }) as HTMLInputElement;
  const aiBox = h("div", { class: "ai", hidden: true });
  const fDruh = h("input", { id: "fDruh", list: "dlDruh", placeholder: "Jabloň", value: doc.druh, maxLength: 80 }) as HTMLInputElement;
  const fOdruda = h("input", { id: "fOdruda", list: "dlOdruda", placeholder: "Jonagold", value: doc.odruda, maxLength: 120 }) as HTMLInputElement;
  const fRok = h("input", { id: "fRok", type: "number", inputmode: "numeric", min: "1900", max: "2100", placeholder: "2024", value: doc.rok ?? "" }) as HTMLInputElement;
  const fPodnoz = h("input", { id: "fPodnoz", list: "dlPodnoz", placeholder: "M9, semenáč…", value: doc.podnoz, maxLength: 80 }) as HTMLInputElement;
  const fPozn = h("textarea", { id: "fPozn", placeholder: "Např. opylovač pro…, roubováno 2023", maxLength: 4000 }) as HTMLTextAreaElement;
  fPozn.value = doc.poznamka;
  const fCislo = h("input", { id: "fCislo", type: "number", inputmode: "numeric", min: "0", value: doc.cislo ?? "" }) as HTMLInputElement;
  const stavChips = h("div", { class: "chips", role: "group", "aria-label": "Stav rostliny" });
  const posHint = h("p", { class: "hint" });
  const delBtn = h("button", { type: "button", class: "btn danger", html: I.trash + "<span>Smazat</span>" });
  const dlDruh = h("datalist", { id: "dlDruh" }), dlOdruda = h("datalist", { id: "dlOdruda" }), dlPodnoz = h("datalist", { id: "dlPodnoz" });

  const form = h("form", { class: "sheet-body", autocomplete: "off" },
    h("div", { class: "field" }, h("span", {}, "Fotky"), photoBox,
      h("div", { class: "row-between" }, h("small", { class: "hint" }, "Další fotka bude:"), kindChips), fileCam, fileGal),
    aiBox,
    h("div", { class: "row2" }, h("label", { class: "field" }, h("span", {}, "Druh"), fDruh), h("label", { class: "field" }, h("span", {}, "Odrůda"), fOdruda)),
    h("div", { class: "row2" }, h("label", { class: "field" }, h("span", {}, "Rok výsadby"), fRok), h("label", { class: "field" }, h("span", {}, "Podnož"), fPodnoz)),
    h("div", { class: "field" }, h("span", {}, "Stav"), stavChips),
    h("label", { class: "field" }, h("span", {}, "Poznámka"), fPozn),
    h("label", { class: "field" }, h("span", {}, "Číslo rostliny"), fCislo),
    h("div", { class: "btns" },
      h("button", { type: "submit", class: "btn primary", onclick: () => { duplicateAfterSave = false; } }, "Uložit"),
      h("button", { type: "submit", class: "btn ghost", onclick: () => { duplicateAfterSave = true; }, html: I.copy + "<span>Uložit a další</span>" })),
    existing ? h("div", { class: "btns" },
      h("button", { type: "button", class: "btn ghost", onclick: move, html: I.pin + "<span>Posunout</span>" }), delBtn) : null,
    posHint, dlDruh, dlOdruda, dlPodnoz);
  sheet.replaceChildren(
    h("div", { class: "sheet-head" }, badge, title, h("button", { class: "close", type: "button", "aria-label": "Zavřít", onclick: close }, "×")),
    form);

  // ---- datalists ----
  const fill = (dl: HTMLElement, vals: Iterable<string>) => dl.replaceChildren(...[...vals].map((v) => h("option", { value: v })));
  const all = [...ctx.plants.values()];
  fill(dlDruh, new Set([...DRUHY.map((d) => d[0]), ...all.map((t) => t.druh).filter(Boolean)]));
  fill(dlPodnoz, new Set([...PODNOZE, ...all.map((t) => t.podnoz).filter(Boolean)]));
  const updOdrudy = () => {
    const d = norm(fDruh.value).trim();
    fill(dlOdruda, new Set(all.filter((t) => t.odruda && (!d || norm(t.druh) === d)).map((t) => t.odruda)));
    badge.style.background = spColor(fDruh.value);
  };
  fDruh.addEventListener("input", updOdrudy); updOdrudy();

  // ---- stav ----
  const drawStav = () => stavChips.replaceChildren(...STAVY.map(([k, l]) =>
    h("button", { type: "button", class: "chip", "aria-pressed": String(stav === k), onclick: () => { stav = k; drawStav(); } }, l)));
  drawStav();

  // ---- photos ----
  const drawKinds = () => kindChips.replaceChildren(...KINDS.filter(([k]) => k !== "jine").map(([k, l]) =>
    h("button", { type: "button", class: "chip", "aria-pressed": String(nextKind === k), onclick: () => { nextKind = k; drawKinds(); } }, l)));
  drawKinds();
  const currentPhotos = () => [
    ...(ctx.photos.get(doc.id) ?? []).filter((p) => !ed.removed.has(p.id)),
    ...ed.newPhotos,
  ];
  const kindOf = (p: Photo) => ed.kinds.get(p.id) ?? p.kind;
  function drawPhotos() {
    const items = currentPhotos().map((p) => {
      const kind = kindOf(p);
      const tag = h("button", { type: "button", class: "tag", title: "Změnit typ fotky", onclick: () => {
        const order = KINDS.map((k) => k[0]); const nk = order[(order.indexOf(kind) + 1) % order.length];
        if (ed.newPhotos.includes(p)) p.kind = nk; else ed.kinds.set(p.id, nk);
        drawPhotos();
      } }, KIND_LABEL[kind]);
      const rm = h("button", { type: "button", class: "rm", "aria-label": "Odebrat fotku", onclick: () => {
        if (ed.newPhotos.includes(p)) ed.newPhotos = ed.newPhotos.filter((x) => x !== p); else ed.removed.add(p.id);
        drawPhotos();
      } }, "×");
      return h("div", { class: "ph" }, h("img", { src: photoSrc(p), alt: KIND_LABEL[kind], loading: "lazy" }), tag, rm);
    });
    photoBox.replaceChildren(...items,
      h("button", { type: "button", class: "addph", onclick: () => fileCam.click(), html: I.camera + "Vyfotit" }),
      h("button", { type: "button", class: "addph", onclick: () => fileGal.click(), html: I.image + "Z galerie" }));
    void drawAi();
  }
  async function onFiles(ev: Event) {
    const input = ev.target as HTMLInputElement;
    const files = [...(input.files ?? [])]; input.value = "";
    for (const f of files) {
      try {
        const [blob, thumb] = await Promise.all([compressImage(f, 1800), compressImage(f, 400, 0.75)]);
        ed.newPhotos.push({ id: uuid(), plant_id: doc.id, garden_id: ctx.garden.id, kind: nextKind, created_at: new Date().toISOString(), blob, thumb });
      } catch { toast("Fotku se nepodařilo načíst."); }
    }
    if (files.length && nextKind === "stitek") { nextKind = "rostlina"; drawKinds(); }
    drawPhotos();
  }
  fileCam.onchange = onFiles; fileGal.onchange = onFiles;

  // ---- Pl@ntNet ----
  let results: Identification[] | null = doc.urceni?.results ?? null;
  async function drawAi() {
    const usable = currentPhotos().filter((p) => kindOf(p) !== "stitek");
    if (!usable.length || !(await canIdentify())) { aiBox.hidden = true; return; }
    aiBox.hidden = false;
    const btn = h("button", { type: "button", class: "btn", html: I.leaf + "<span>Určit druh z fotek</span>" });
    btn.onclick = async () => {
      btn.setAttribute("disabled", ""); btn.lastElementChild!.textContent = "Určuji…";
      try {
        const fd = new FormData();
        for (const p of usable.slice(-5)) {
          let blob = p.blob;
          if (!blob) { const r = await fetch("/api/photos/" + p.id); if (r.ok) blob = await r.blob(); }
          if (blob) fd.append("image_" + kindOf(p), blob, "foto.jpg");
        }
        const r = await api<{ results: Identification[] }>("POST", "/api/identify", fd);
        results = r.results;
        doc.urceni = { at: new Date().toISOString(), results };
        if (!results.length) toast("Druh se nepodařilo určit. Zkus detail listu nebo plodu.");
      } catch (e) {
        toast(e instanceof ApiError && e.status === 0 ? "Určení druhu potřebuje internet." : (e as Error).message);
      }
      void drawAi();
    };
    const list = results?.length ? h("ul", {}, ...results.slice(0, 3).map((r) => {
      const cz = druhFromLatin(r.latin, r.names);
      return h("li", {}, h("button", { type: "button", onclick: () => { fDruh.value = cz; updOdrudy(); toast("Druh nastaven: " + cz); } },
        h("span", {}, h("b", {}, cz), " ", h("small", {}, r.latin)), h("span", { class: "score" }, r.score + " %")));
    })) : null;
    aiBox.replaceChildren(
      h("p", {}, results?.length ? "Pl@ntNet navrhuje (klepnutím doplníš druh). Odrůdu z fotky určit nejde, tu vyfoť ze štítku." : "Z fotek listu, květu, plodu nebo kůry umím odhadnout druh."),
      ...(list ? [list] : []), btn);
  }
  drawPhotos();

  // ---- position ----
  const updPos = () => {
    if (ctx.garden.mode === "photo") {
      posHint.textContent = "Umístěno na fotce zahrady";
      return;
    }
    const p = parcelAt(ctx.garden.parcels, doc.lat, doc.lng);
    posHint.textContent = "Poloha " + doc.lat.toFixed(6) + ", " + doc.lng.toFixed(6) + (p ? " · parcela " + p.cislo : " · mimo parcely zahrady");
  };
  updPos();
  function move() {
    ctx.startPlacing("move", L.latLng(doc.lat, doc.lng), (ll) => {
      doc.lat = +ll.lat.toFixed(8); doc.lng = +ll.lng.toFixed(8); updPos();
      ctx.showSheet("formSheet"); toast("Nová poloha se uloží tlačítkem Uložit.");
    }, () => ctx.showSheet("formSheet"), doc.id);
  }

  // ---- save / delete ----
  form.addEventListener("submit", async (ev) => {
    ev.preventDefault();
    doc.druh = fDruh.value.trim(); doc.odruda = fOdruda.value.trim();
    doc.rok = fRok.value ? Number(fRok.value) : null; doc.podnoz = fPodnoz.value.trim();
    doc.poznamka = fPozn.value.trim(); doc.stav = stav;
    doc.cislo = fCislo.value ? Number(fCislo.value) : ctx.nextNumber();
    doc.parcela = ctx.garden.mode === "photo" ? "" : (parcelAt(ctx.garden.parcels, doc.lat, doc.lng)?.cislo ?? "");
    doc.updated_at = new Date().toISOString();
    await store.putPlant(doc);
    await store.queue({ type: "plant", id: doc.id, garden_id: doc.garden_id });
    for (const p of ed.newPhotos) {
      await store.putPhoto(p);
      await store.queue({ type: "photo", id: p.id, garden_id: p.garden_id });
    }
    for (const [pid, kind] of ed.kinds) {
      const p = await store.getPhoto(pid);
      if (p) await store.putPhoto({ ...p, kind });
    }
    for (const pid of ed.removed) {
      const p = await store.getPhoto(pid);
      if (!p) continue;
      await store.putPhoto({ ...p, deleted_at: new Date().toISOString() });
      if (p.uploaded) await store.queue({ type: "photo-delete", id: pid, garden_id: p.garden_id });
      else await store.unqueue("photo:" + pid);
    }
    close();
    await ctx.reload();
    void syncSoon();
    if (duplicateAfterSave) {
      toast("Uloženo – umísti křížek pro další stejnou rostlinu");
      ctx.startPlacing("new", L.latLng(doc.lat, doc.lng), (nextLl) => openForm(ctx, null, nextLl, doc));
    } else {
      toast(navigator.onLine ? "Uloženo" : "Uloženo v telefonu, odešle se po připojení");
    }
  });

  let armed = false;
  delBtn.onclick = async () => {
    if (!armed) {
      armed = true; delBtn.lastElementChild!.textContent = "Klepni znovu pro smazání";
      setTimeout(() => { armed = false; delBtn.lastElementChild!.textContent = "Smazat"; }, 4000);
      return;
    }
    await store.putPlant({ ...doc, deleted_at: new Date().toISOString() });
    await store.unqueue("plant:" + doc.id);
    await store.queue({ type: "plant-delete", id: doc.id, garden_id: doc.garden_id });
    close();
    await ctx.reload();
    toast("Rostlina smazána");
    void syncSoon();
  };

  function close() { ctx.select(null); ctx.showSheet(null); }
  ctx.showSheet("formSheet");
  form.scrollTop = 0;
}
